import React, { useRef, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

// Sensitivity multipliers for better mobile UX
const PAN_SENSITIVITY = 2.0;  // Move 2x pixels per 1px gesture
const ZOOM_SENSITIVITY = 1.5; // Exponential zoom for responsive feel

interface NativeCropModalProps {
  isOpen: boolean;
  imageSrc: string;
  onCropComplete: (croppedFile: File) => void;
  onClose: () => void;
}

export const NativeCropModal: React.FC<NativeCropModalProps> = ({
  isOpen,
  imageSrc,
  onCropComplete,
  onClose,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const dragStartRef = useRef({ x: 0, y: 0, startPos: { x: 0, y: 0 } });
  const pinchStartRef = useRef({ distance: 0, zoom: 1 });
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen || !imageSrc) return;

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      drawCanvas();
    };
    img.src = imageSrc;
  }, [isOpen, imageSrc]);

  useEffect(() => {
    if (imageRef.current) {
      drawCanvas();
    }
  }, [zoom, position]);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Fixed 1:1 aspect ratio (square)
    const targetWidth = 1000;
    const targetHeight = 1000;

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // Scale to fill canvas - allows user to pan and select 1:1 area
    const scale = Math.max(
      targetWidth / img.width,
      targetHeight / img.height
    ) * zoom;

    const scaledWidth = img.width * scale;
    const scaledHeight = img.height * scale;

    // Apply position offset (pan)
    const baseX = (targetWidth - scaledWidth) / 2;
    const baseY = (targetHeight - scaledHeight) / 2;
    
    // Calculate boundaries to prevent empty spaces
    const maxOffsetX = Math.max(0, (scaledWidth - targetWidth) / 2);
    const maxOffsetY = Math.max(0, (scaledHeight - targetHeight) / 2);
    
    const clampedX = Math.max(-maxOffsetX, Math.min(maxOffsetX, position.x));
    const clampedY = Math.max(-maxOffsetY, Math.min(maxOffsetY, position.y));
    
    const x = baseX + clampedX;
    const y = baseY + clampedY;

    // Clear and draw
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
  };

  // Mouse handlers (desktop)
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startPos: { ...position }
    };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    animationFrameRef.current = requestAnimationFrame(() => {
      const deltaX = (e.clientX - dragStartRef.current.x) * PAN_SENSITIVITY;
      const deltaY = (e.clientY - dragStartRef.current.y) * PAN_SENSITIVITY;
      
      setPosition({
        x: dragStartRef.current.startPos.x + deltaX,
        y: dragStartRef.current.startPos.y + deltaY
      });
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers (mobile)
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 1) {
      // Single finger - pan
      setIsDragging(true);
      const touch = e.touches[0];
      dragStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        startPos: { ...position }
      };
    } else if (e.touches.length === 2) {
      // Two fingers - pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
      pinchStartRef.current = { distance, zoom };
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    
    if (e.touches.length === 1 && isDragging) {
      // Pan with single finger
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      animationFrameRef.current = requestAnimationFrame(() => {
        const touch = e.touches[0];
        const deltaX = (touch.clientX - dragStartRef.current.x) * PAN_SENSITIVITY;
        const deltaY = (touch.clientY - dragStartRef.current.y) * PAN_SENSITIVITY;
        
        setPosition({
          x: dragStartRef.current.startPos.x + deltaX,
          y: dragStartRef.current.startPos.y + deltaY
        });
      });
    } else if (e.touches.length === 2) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
      
      const scale = distance / pinchStartRef.current.distance;
      
      // Apply exponential scaling for more responsive zoom
      const sensitiveScale = scale > 1 
        ? Math.pow(scale, ZOOM_SENSITIVITY)
        : 1 / Math.pow(1 / scale, ZOOM_SENSITIVITY);
      
      const newZoom = Math.max(1, Math.min(3, pinchStartRef.current.zoom * sensitiveScale));
      setZoom(newZoom);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleConfirm = async () => {
    if (!canvasRef.current || isProcessing) return;

    setIsProcessing(true);
    try {
      // Canvas já é 1000x1000 com apenas a imagem - copiar diretamente
      const blob = await new Promise<Blob | null>((resolve) => {
        canvasRef.current!.toBlob(resolve, 'image/jpeg', 0.9);
      });

      if (blob) {
        const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
        onCropComplete(file);
      }
    } catch (error) {
      console.error('Crop error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md w-[calc(100%-2rem)] p-0 gap-0 bg-gray-900 border-white/10">
        <DialogDescription className="sr-only">
          Modal para ajustar e fazer crop da foto de perfil
        </DialogDescription>
        <div className="p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Ajustar Foto</h2>
          <p className="text-sm text-white/60">Use o controle para ajustar o zoom</p>
        </div>

        <div className="p-4 flex flex-col items-center justify-center bg-black gap-2">
              <p className="text-xs text-white/60 text-center">
                Arraste para posicionar • Pinça para zoom
              </p>
          <div className="relative">
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              className="max-w-full max-h-[60vh] w-auto h-auto"
              style={{
                cursor: isDragging ? 'grabbing' : 'grab',
                touchAction: 'none',
                userSelect: 'none'
              }}
            />
          </div>
        </div>

        <div className="p-4 border-t border-white/10 space-y-4">
          <div className="space-y-2">
            <label className="text-sm text-white/80">Zoom</label>
            <Slider
              value={[zoom]}
              onValueChange={([value]) => setZoom(value)}
              min={1}
              max={3}
              step={0.1}
              className="w-full"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
              className="flex-1 border-white/30 text-white bg-white/10 hover:bg-white/20"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isProcessing}
              className="flex-1 bg-luxury-gold hover:bg-luxury-gold/90 text-black"
            >
              {isProcessing ? 'Processando...' : 'Confirmar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
